'use client';

import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { useCallback, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { createAuthClientAdapter } from '@/lib/auth/auth-client-adapter';
import { authClient } from '@/lib/auth-client';
import { cn } from '@/lib/utils';
import { HeroSection } from './components/HeroSection';
import type { EvidenceColumn, FeatureSection, WorkflowStep } from './types';
interface FeaturesPageClientProperties {
  evidenceColumns: EvidenceColumn[];
  featureSections: FeatureSection[];
  workflowSteps: WorkflowStep[];
}
export function FeaturesPageClient({
  featureSections,
  evidenceColumns,
  workflowSteps
}: FeaturesPageClientProperties) {
  const [isLoading, setIsLoading] = useState(false);
  const login = useCallback(async () => {
    setIsLoading(true);
    try {
      const safeAuthClient = createAuthClientAdapter(authClient);
      await safeAuthClient.signInSocial({
        provider: 'twitter',
        callbackURL: '/timeline'
      });
    } catch (error) {
      console.error('Failed to start Twitter login from features page', error);
    } finally {
      setIsLoading(false);
    }
  }, []);
  const handleLogin = useCallback(() => {
    login().catch((error_0: unknown) => {
      console.error('Login failed from features page', error_0);
    });
  }, [login]);
  return <>
      <HeroSection onLogin={handleLogin} isLoading={isLoading} />
      <JourneySections sections={featureSections} />
      <EvidenceGrid columns={evidenceColumns} />
      <WorkflowPanel steps={workflowSteps} />

      <section className="px-4 py-20 bg-muted/30">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold">What people notice first</h2>
          <p className="mt-3 text-base text-muted-foreground">
            Sweetistics becomes your home screen for staying sharp on Twitter—whether you operate solo or loop others in
            later.
          </p>
          <div className="mt-10 grid gap-6 md:grid-cols-3 text-left">
            {[{
            title: 'A calmer home feed',
            description: 'Timeline coverage metrics show exactly when you need a new sync so doomscrolling turns into quick scans.'
          }, {
            title: 'Trustworthy follow decisions',
            description: 'Friendimizer highlights why each suggestion exists—last tweet, engagement delta, and mutuals—so you never wonder where a rec appeared from.'
          }, {
            title: 'No more missed changes',
            description: 'Pulse pings when execs change bios, when investors drop you from lists, or when competitors link a new launch page.'
          }].map(item => <div key={item.title} className="rounded-2xl border border-border/60 bg-card/80 p-6 shadow-sm dark:bg-background/70">
                <h3 className="text-xl font-semibold text-foreground">{item.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground">{item.description}</p>
              </div>)}
          </div>
        </div>
      </section>

      <section className="px-4 py-20">
        <div className="max-w-3xl mx-auto text-center space-y-6">
          <h2 className="text-3xl md:text-4xl font-bold">Ready to make your timeline useable again?</h2>
          <p className="text-base text-muted-foreground">
            Sweetistics gives you a single source of truth for your feed, your relationships, and the profile shifts
            that matter. Connect in seconds—no browser extension required.
          </p>
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <Button onClick={handleLogin} disabled={isLoading} className="px-8" size="lg">
              {isLoading ? 'Connecting…' : 'Connect with Twitter'}
            </Button>
            <Button variant="outline" asChild size="lg">
              <Link href="/pricing" className="px-8">
                View pricing
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </>;
}
function JourneySections(t0) {
  const $ = _c(4);
  const {
    sections
  } = t0;
  let t1;
  if ($[0] !== sections) {
    t1 = sections.map(_temp3);
    $[0] = sections;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <section className="px-4 py-20"><div className="mx-auto flex max-w-6xl flex-col gap-16">{t1}</div></section>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp3(section, index) {
  const isEven = index % 2 === 0;
  return <div key={section.id} className={cn("flex flex-col gap-10 rounded-3xl border border-border/60 bg-card/80 p-8 shadow-xl dark:bg-card/80 md:flex-row md:items-center", !isEven && "md:flex-row-reverse")}><div className="flex-1 space-y-5"><Badge variant="outline" className="w-fit rounded-full border-primary/30 text-primary">{section.kicker}</Badge><h2 className="text-3xl font-semibold text-foreground md:text-4xl">{section.title}</h2><p className="text-base text-muted-foreground">{section.description}</p><ul className="space-y-3">{section.bullets.map(_temp)}</ul></div><div className="flex-1"><div className="relative overflow-hidden rounded-2xl border border-border/60 bg-card/80 p-6 shadow-inner"><p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{section.calloutTitle}</p><ul className="mt-4 space-y-3 text-sm text-foreground/90">{section.calloutLines.map(_temp2)}</ul></div></div></div>;
}
function _temp2(line) {
  return <li key={line} className="rounded-lg bg-muted/40 px-3 py-2">{line}</li>;
}
function _temp(bullet) {
  return <li key={bullet} className="flex gap-3 text-sm text-muted-foreground"><span className="mt-1 block h-2 w-2 rounded-full bg-primary" aria-hidden={true} /><span>{bullet}</span></li>;
}
function EvidenceGrid(t0) {
  const $ = _c(5);
  const {
    columns
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="mb-12 text-center"><h2 className="text-3xl md:text-4xl font-bold">Why operators rely on Sweetistics</h2><p className="mt-3 text-base text-muted-foreground">These are the tangible outcomes customers mention after replacing spreadsheets and ad-hoc lists with our stack.</p></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== columns) {
    t2 = columns.map(_temp5);
    $[1] = columns;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <section className="px-4 py-20 bg-muted/20 bg-card/80"><div className="mx-auto max-w-6xl">{t1}<div className="grid gap-6 md:grid-cols-3">{t2}</div></div></section>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function _temp5(column) {
  return <div key={column.title} className="rounded-2xl border border-border/50 bg-card/80 p-6 shadow-sm dark:bg-background/70"><h3 className="text-lg font-semibold text-foreground">{column.title}</h3><ul className="mt-4 space-y-3 text-sm text-muted-foreground">{column.items.map(_temp4)}</ul></div>;
}
function _temp4(item) {
  return <li key={item} className="flex gap-2"><span className="mt-1 block h-1.5 w-1.5 rounded-full bg-primary" aria-hidden={true} /><span>{item}</span></li>;
}
function WorkflowPanel(t0) {
  const $ = _c(5);
  const {
    steps
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <h2 className="text-2xl font-semibold text-foreground md:text-3xl">How you run Sweetistics each week</h2>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== steps) {
    t2 = steps.map(_temp6);
    $[1] = steps;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <section className="px-4 pb-20"><div className="mx-auto max-w-4xl rounded-3xl border border-border/60 bg-card/80 p-10 shadow-xl dark:bg-background/70">{t1}<div className="mt-8 grid gap-6 md:grid-cols-3">{t2}</div></div></section>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function _temp6(step, index) {
  return <div key={step.title} className="space-y-3"><span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">{index + 1}</span><h3 className="text-lg font-semibold text-foreground">{step.title}</h3><p className="text-sm text-muted-foreground">{step.description}</p></div>;
}